import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShieldAlert, ShieldCheck, ShieldQuestion, ExternalLink, ScanLine } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { normVerdict, hrefFor } from "@/lib/shield";
import { cn } from "@/lib/utils";

const COPY = {
  blocked: { icon: ShieldAlert, title: "Shield blocked this link", body: "It imitates a known brand or uses a lookalike domain. Scammers use links like this to steal wallets and passwords.", cls: "border-destructive/40 bg-destructive/10 text-destructive" },
  warning: { icon: ShieldAlert, title: "Open with caution", body: "This link hides its destination or uses a risky domain. Only continue if you trust who sent it.", cls: "border-amber-500/40 bg-amber-500/10 text-amber-400" },
  unknown: { icon: ShieldQuestion, title: "Unverified link", body: "Shield hasn't seen this domain before. Check the address before you continue.", cls: "border-white/15 bg-white/5 text-white/85" },
  safe: { icon: ShieldCheck, title: "Verified domain", body: "", cls: "border-bloq-mint/30 bg-bloq-mint/10 text-bloq-mint" },
};

// §3.2l / §9.3 link interstitial: caution / unknown / blocked links never open directly.
// "Open anyway" is hidden for blocked; "Scan with Shield" deep-links to /app/shield?scan=<url>.
export function LinkGate() {
  const [link, setLink] = useState(null);
  const navigate = useNavigate();
  useEffect(() => {
    const on = (e) => setLink(e.detail || null);
    window.addEventListener("bloq:linkgate", on);
    return () => window.removeEventListener("bloq:linkgate", on);
  }, []);
  if (!link) return null;
  const v = normVerdict(link.verdict);
  const c = COPY[v];
  const Icon = c.icon;
  return (
    <Dialog open onOpenChange={(o) => !o && setLink(null)}>
      <DialogContent className="max-w-sm border-white/10 bg-bloq-s1 text-white" data-testid="link-gate">
        <div className={cn("flex items-start gap-3 rounded-2xl border p-3.5", c.cls)}>
          <Icon className="mt-0.5 h-5 w-5 shrink-0" />
          <div className="min-w-0">
            <DialogTitle className="text-sm font-bold leading-normal" data-testid="link-gate-title">{c.title}</DialogTitle>
            <div className="mt-0.5 text-xs opacity-90">{link.label}</div>
          </div>
        </div>
        <div className="mt-3 break-all rounded-xl border border-white/10 bg-bloq-bg px-3 py-2 font-mono text-xs text-white/90" data-testid="link-gate-url">{link.url}</div>
        {c.body && <p className="mt-2 text-sm text-bloq-muted">{c.body}</p>}
        <div className="mt-4 flex flex-col gap-2">
          <button
            onClick={() => { setLink(null); navigate(`/app/shield?scan=${encodeURIComponent(hrefFor(link.url))}`); }}
            className="flex items-center justify-center gap-2 rounded-full bloq-gradient py-2.5 text-sm font-bold text-bloq-bg hover:opacity-90"
            data-testid="link-gate-scan-btn"
          >
            <ScanLine className="h-4 w-4" /> Scan with Shield
          </button>
          {v !== "blocked" && (
            <a
              href={hrefFor(link.url)}
              target="_blank"
              rel="noopener noreferrer nofollow"
              onClick={() => setLink(null)}
              className="flex items-center justify-center gap-2 rounded-full border border-white/15 py-2.5 text-sm font-semibold text-white hover:bg-white/5"
              data-testid="link-gate-open-btn"
            >
              <ExternalLink className="h-4 w-4" /> Open anyway
            </a>
          )}
          <button onClick={() => setLink(null)} className="rounded-full py-2 text-sm text-bloq-muted hover:text-white" data-testid="link-gate-cancel-btn">Cancel</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
